// @cogo/template — canonical JSON serialization, slot/design masking, structureHash
//
// PURE leaf module: no node:fs, no network, no Date.now/Math.random. Uses
// WebCrypto (`globalThis.crypto.subtle`) for SHA-256, which is async.

import type { AppBundle, SlotAddress, SlotDecl, TemplatePackage, Json } from './types'
import { applyAtAddress } from './resolve-address'

/**
 * Serializes a Json value to a canonical string form: object keys sorted
 * lexicographically at every level, no extraneous whitespace, arrays kept
 * in declaration order. Byte-stable across repeated calls for the same
 * logical value regardless of source key order.
 *
 * Throws on `undefined` and non-finite numbers (NaN/Infinity) — neither is
 * valid JSON.
 */
export function canonicalJson(value: Json): string {
  return serialize(value)
}

function serialize(value: unknown): string {
  if (value === undefined) {
    throw new TypeError('canonicalJson: undefined is not a valid JSON value')
  }
  if (value === null) {
    return 'null'
  }
  const t = typeof value
  if (t === 'boolean') {
    return value ? 'true' : 'false'
  }
  if (t === 'number') {
    if (!Number.isFinite(value as number)) {
      throw new TypeError(`canonicalJson: non-finite number is not a valid JSON value (${String(value)})`)
    }
    return JSON.stringify(value)
  }
  if (t === 'string') {
    return JSON.stringify(value)
  }
  if (Array.isArray(value)) {
    return '[' + value.map((item) => serialize(item)).join(',') + ']'
  }
  if (t === 'object') {
    const obj = value as { [k: string]: unknown }
    const keys = Object.keys(obj).sort()
    const parts = keys.map((key) => `${JSON.stringify(key)}:${serialize(obj[key])}`)
    return '{' + parts.join(',') + '}'
  }
  throw new TypeError(`canonicalJson: unsupported value type "${t}"`)
}

// Address resolution/write is shared with instantiate.ts via
// resolve-address.ts (see that module for the JSON Pointer + element-tree
// write logic) — this thin wrapper exists only to keep call sites below
// reading as `applyMask(clone, address, maskToken)`.
function applyMask(bundle: AppBundle, address: SlotAddress, maskToken: string): void {
  applyAtAddress(bundle.files, address, maskToken)
}

/**
 * Returns a deep-cloned copy of `bundle` with every declared slot's value
 * replaced by a deterministic mask token (`" slot:" + slotId`), and the
 * design-system file replaced with `" design-binding"` when rebinding is
 * `"allowed"`. Masking makes the resulting structure independent of the
 * actual slot content — only the app's structural shape remains.
 */
export function maskBundle(bundle: AppBundle, slots: SlotDecl[], design: TemplatePackage['designBinding']): AppBundle {
  const clone: AppBundle = structuredClone(bundle)
  for (const slot of slots) {
    const maskToken = ' slot:' + slot.slotId
    const addresses = Array.isArray(slot.address) ? slot.address : [slot.address]
    for (const address of addresses) {
      applyMask(clone, address, maskToken)
    }
  }
  if (design.rebinding === 'allowed') {
    if (clone.files[design.file] === undefined) {
      throw new TypeError(`maskBundle: design file "${design.file}" not found`)
    }
    clone.files[design.file] = ' design-binding'
  }
  return clone
}

function toHex(bytes: Uint8Array): string {
  let out = ''
  for (const b of bytes) {
    out += b.toString(16).padStart(2, '0')
  }
  return out
}

/**
 * Computes a structure-only hash of `bundle`: mask every declared slot value
 * and the design-system file, canonicalize the resulting file map, then
 * SHA-256 the UTF-8 bytes. The result is stable across slot-content edits
 * and changes only when the app's structural shape changes.
 */
export async function computeStructureHash(
  bundle: AppBundle,
  slots: SlotDecl[],
  design: TemplatePackage['designBinding'],
): Promise<string> {
  const masked = maskBundle(bundle, slots, design)
  const canonical = canonicalJson(masked.files)
  const bytes = new TextEncoder().encode(canonical)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return `sha256:${toHex(new Uint8Array(digest))}`
}
